import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  VStack, 
  Heading, 
  Text, 
  useToast, 
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Reviewer-invite handoff: if the user landed here from
  // /reviews/accept/:token, that page tacked `?invite=<token>` onto the
  // URL. We round-trip it through login so the accept handshake can
  // resume after auth without the user having to re-click the email link.
  const searchParams = new URLSearchParams(location.search);
  const inviteToken = searchParams.get('invite');
  // Event-review invites use a distinct param so the bounce target differs
  // (/events/accept/:token vs the campaign /reviews/accept/:token).
  const eventInviteToken = searchParams.get('eventInvite');
  const buildPostLoginRoute = (defaultRoute) => {
    if (eventInviteToken) return `/events/accept/${encodeURIComponent(eventInviteToken)}`;
    if (!inviteToken) return defaultRoute;
    return `/reviews/accept/${encodeURIComponent(inviteToken)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        if (inviteToken || eventInviteToken) {
          // Reviewer-invite flow always trumps onboarding — the invitee
          // is here to review someone else's campaign / event, not to build one.
          navigate(buildPostLoginRoute('/'));
        } else if (result.needsOnboarding) {
          navigate('/campaign/new?welcome=true');
        } else {
          navigate('/');
        }
      } else {
        toast({
          title: 'Login failed',
          description: result.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'An error occurred',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex align="center" justify="center" minH="100vh" bg="#0a0a0a">
      <Box 
        maxW="md" 
        w="100%" 
        p={8}
        borderRadius="12px"
        border="1px solid rgba(255,255,255,0.10)"
        bg="#0f0f0f"
      >
        <VStack spacing={6}>
          <Box mb={2}>
            <img 
              src="/gillyreach-favicon.png" 
              alt="GillyReach" 
              style={{ height: '52px', width: 'auto', margin: '0 auto' }}
            />
          </Box>
          <Heading
            size="lg"
            color="#fafafa"
            fontWeight="700"
            letterSpacing="-0.02em"
          >
            Sign in
          </Heading>
          
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <VStack spacing={4} align="stretch">
              <FormControl id="email" isRequired>
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Work Email</FormLabel>
                <Input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  placeholder="your@work-email.com"
                  bg="#171717"
                  borderColor="rgba(255,255,255,0.12)"
                  color="#fafafa"
                  fontSize="sm"
                  _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                  _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                  _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                />
              </FormControl>
              
              <FormControl id="password" isRequired>
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Password</FormLabel>
                <InputGroup>
                  <Input 
                    type={showPassword ? 'text' : 'password'} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    bg="#171717"
                    borderColor="rgba(255,255,255,0.12)"
                    color="#fafafa"
                    fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                    _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={showPassword ? <FiEyeOff /> : <FiEye />}
                      size="sm"
                      variant="ghost"
                      color="#a3a3a3"
                      onClick={() => setShowPassword(!showPassword)}
                      _hover={{ color: '#fafafa', bg: 'transparent' }}
                      _focus={{ boxShadow: 'none' }}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              
              <Button 
                type="submit"
                bg="#fafafa"
                color="#0a0a0a"
                borderRadius="full"
                fontWeight="600"
                fontSize="sm"
                h="40px"
                width="full" 
                mt={2}
                isLoading={isLoading}
                loadingText="Signing in..."
                _hover={{ opacity: 0.85 }}
              >
                Sign in
              </Button>
            </VStack>
          </form>
          
          <VStack spacing={1}>
            <Text fontSize="sm" color="#a3a3a3">
              Don&apos;t have an account?{' '}
              <Link
                to={eventInviteToken ? `/register?eventInvite=${encodeURIComponent(eventInviteToken)}` : inviteToken ? `/register?invite=${encodeURIComponent(inviteToken)}` : '/register'}
                style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'underline' }}
              >
                Register
              </Link>
            </Text>
            <Text fontSize="sm">
              <Link to="/forgot-password" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Forgot password?
              </Link>
            </Text>
          </VStack>
        </VStack>
      </Box>
    </Flex>
  );
};

export default LoginPage;
